import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Body } from 'app/components/Typography';
import { H4 } from 'app/components/Typography';
import { Button } from 'app/components/BasicButtons/Button';
import { IconButton } from 'app/components/BasicButtons/IconButton';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import clsx from 'clsx';
import { translations } from 'locales/translations';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImportResult } from '../Actions/Types';

export const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '100%',
    },
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    containerRows: {
      display: 'flex',
      flexDirection: 'column',
      width: '300px',
    },
    row: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    rowData: {
      display: 'flex',
      width: '100%',
      margin: '3px',
    },
    divider: {
      backgroundColor: theme.palette.divider,
    },
    color: {
      color: theme.palette.primary.main,
    },
  }),
);

interface RowDataProps {
  Name: string;
  Value: string;
}

function RowData(props: RowDataProps) {
  const classes = useStyles();
  return (
    <Box className={clsx(classes.rowData)}>
      <Box style={{ flexGrow: 2, textAlign: 'left' }}> {props.Name} </Box>
      <Box>
        <Body bold={true}>{props.Value}</Body>
      </Box>
    </Box>
  );
}
export interface PopupImportExportStep5Props {
  state: ImportResult;
  newRows: number;
  existingRows: number;
  errors: number;
  errorMessage?: string;
  handleDownloadImportErrorReport: () => void;
  onClick: () => void;
}
export function PopupImportExportStep5(props: PopupImportExportStep5Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const content: React.ReactNode = React.useMemo((): React.ReactNode => {
    switch (props.state) {
      case ImportResult.success:
        return (
          <>
            <Box className={clsx(classes.containerRows)}>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <Icon
                  style={{ marginBottom: '20px' }}
                  icon={['fas', 'check-circle']}
                  color={'success'}
                  size="3x"
                />
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <H4>{t(translations.ImportSuccessfullyCompleted)}</H4>
              </Box>
              <Box
                style={{ marginTop: '20px' }}
                className={clsx(classes.divider)}
                height={2}
              ></Box>
              <RowData
                Name={t(translations.NewRecordsImported)}
                Value={props.newRows.toString()}
              />
              <RowData
                Name={t(translations.ExistingRecordsUpdated)}
                Value={props.existingRows.toString()}
              />
              <RowData
                Name={t(translations.ErrorsDuringImport)}
                Value={props.errors.toString()}
              />
              <Box className={clsx(classes.row)} style={{ marginTop: '10px' }}>
                <Box>
                  <IconButton
                    onClick={() => props.handleDownloadImportErrorReport()}
                    title={t(translations.DownloadImportErrorReport)}
                    variant="ghost"
                    shape="square"
                    aria-label="close"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    <Icon icon="download" color="primary" />
                    <Box className={clsx(classes.color)}>
                      {t(translations.DownloadDetailedImportResults)}
                    </Box>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </>
        );
      case ImportResult.error:
        return (
          <>
            <Box className={clsx(classes.containerRows)}>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <Icon
                  style={{ marginBottom: '20px' }}
                  icon={['fas', 'exclamation-circle']}
                  color={'accent'}
                  size="3x"
                />
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <H4>{t(translations.ImportCompletedWithErrors)}</H4>
              </Box>
              <Box
                style={{ marginTop: '20px' }}
                className={clsx(classes.divider)}
                height={2}
              ></Box>
              <RowData
                Name={t(translations.NewRecordsImported)}
                Value={props.newRows.toString()}
              />
              <RowData
                Name={t(translations.ExistingRecordsUpdated)}
                Value={props.existingRows.toString()}
              />
              <RowData
                Name={t(translations.ErrorsDuringImport)}
                Value={props.errors.toString()}
              />
              <Box className={clsx(classes.row)} style={{ marginTop: '10px' }}>
                <Box>
                  <IconButton
                    onClick={() => props.handleDownloadImportErrorReport()}
                    title={t(translations.DownloadImportErrorReport)}
                    variant="ghost"
                    shape="square"
                    aria-label="close"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    <Icon icon="download" color="primary" />
                    <Box className={clsx(classes.color)}>
                      {t(translations.DownloadImportErrorReport)}
                    </Box>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </>
        );
      case ImportResult.failed:
        return (
          <>
            <Box className={clsx(classes.containerRows)}>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <Icon
                  style={{ marginBottom: '20px' }}
                  icon={['fas', 'times-circle']}
                  color={'danger'}
                  size="3x"
                />
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <H4>{t(translations.ImportHasFailed)}</H4>
              </Box>
              <Box
                style={{ marginTop: '20px' }}
                className={clsx(classes.divider)}
                height={2}
              ></Box>
              <Box>{t(translations.ImportErrorFailedMessage)}</Box>
              {props.errorMessage !== undefined && (
                <Box style={{ marginTop: '10px' }}>
                  {t(translations.ImportAdditionalDetails)}:{' '}
                  {props.errorMessage}
                </Box>
              )}
            </Box>
          </>
        );
      case ImportResult.default:
        return (
          <>
            <Icon
              icon={['fas', 'question-circle']}
              color={'default'}
              size="3x"
            />
          </>
        );
      default:
        return (
          <>
            <Icon
              icon={['fas', 'question-circle']}
              color={'default'}
              size="3x"
            />
          </>
        );
    }
  }, [
    classes.color,
    classes.containerRows,
    classes.divider,
    classes.row,
    props,
    t,
  ]);
  return (
    <>
      <Box className={clsx(classes.root)}>
        <Box style={{ height: '85%' }} className={clsx(classes.container)}>
          {content}
        </Box>
        <Box style={{ height: '15%' }} className={clsx(classes.container)}>
          <Button variant="main" onClick={props.onClick}>
            {t(translations.ImportBackToPage)}
          </Button>
        </Box>
      </Box>
    </>
  );
}
